/**
 * Downscale + recompress an image File into a small JPEG "preview" for the
 * server-side vision pre-pass. Keeps the longest edge <= maxEdge.
 *
 * This is ONLY the cheap copy the vision model looks at — the original file is
 * uploaded separately at full quality and is what generated code references.
 * Mirrors the Pexels pattern (a small image to the vision model, the full-res
 * one in the actual site), but since our storage can't resize via URL params we
 * do the downscale here in the browser. Falls back to the original File if the
 * browser can't decode/encode it.
 */
export async function compressImageFile(
  file: File,
  { maxEdge = 1024, quality = 0.72 }: { maxEdge?: number; quality?: number } = {},
): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      bitmap.close?.();

      return file;
    }

    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

    if (!blob) {
      return file;
    }

    const name = `${file.name.replace(/\.[^.]+$/, '')}-preview.jpg`;

    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    // If anything goes wrong (unsupported format, OOM, etc.) fall back to the
    // original — the upload path will still work, just without the size win.
    return file;
  }
}
